import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiSourceRequestTabs from '../components/ApiSourceRequestTabs';
import { runIngestionApiTest, fetchIngestionSettingsSourcesPage } from '../utils/api';
import {
  createEditorRow,
  emptyHarnessRows,
  harnessDeltaFromRows,
  harnessOverridesFromRows,
  harnessRowsFromGroup,
  resolveHttpMethod,
} from '../utils/apiQueryParams';
import '../components/ApiSourceRequestTabs.css';

const HARNESS_LS_PREFIX = 'cascade.harnessApiParams';

function loadHarnessState(suffix, groupSlug) {
  try {
    const raw = localStorage.getItem(`${HARNESS_LS_PREFIX}.${suffix}.${groupSlug}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function saveHarnessState(suffix, groupSlug, state) {
  try {
    const key = `${HARNESS_LS_PREFIX}.${suffix}.${groupSlug}`;
    const hasAny = state
      && typeof state === 'object'
      && Object.values(state).some((section) => section && Object.keys(section).length);
    if (!hasAny) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(state));
  } catch (_) {
    /* ignore */
  }
}

function RequestDebugBlock({ requestDebug, responseBody, httpStatus, defaultOpen = false }) {
  if (!requestDebug) return null;

  return (
    <details className="ingestion-api-response-block ingestion-api-request-debug" open={defaultOpen}>
      <summary>Request sent {httpStatus ? `(HTTP ${httpStatus})` : ''}</summary>
      <ul className="ingestion-api-step-summary">
        <li><strong>Method</strong> {requestDebug.method}</li>
        <li><strong>URL</strong> <code>{requestDebug.requestUrl || requestDebug.url}</code></li>
        <li>
          <strong>Auth</strong> {requestDebug.authType || 'none'}
          {requestDebug.authConfigured === false ? ' (not configured — check Auth tab in group settings)' : ''}
        </li>
      </ul>
      {requestDebug.headersPreview && requestDebug.headersPreview !== '{}' && (
        <>
          <strong>Headers</strong>
          <pre className="ingestion-api-response-json">{requestDebug.headersPreview}</pre>
        </>
      )}
      {requestDebug.method === 'POST' && (
        <>
          <strong>Body</strong>
          <pre className="ingestion-api-response-json">
            {requestDebug.bodyPreview && requestDebug.bodyPreview !== '{}'
              ? requestDebug.bodyPreview
              : (requestDebug.bodyRaw || '(empty — check Body tab in group settings)')}
          </pre>
        </>
      )}
      {requestDebug.pathPreview && requestDebug.method === 'GET' && (
        <>
          <strong>Query path</strong>
          <pre className="ingestion-api-response-json">{requestDebug.pathPreview}</pre>
        </>
      )}
      {requestDebug.curlPreview && (
        <>
          <strong>cURL</strong>
          <pre className="ingestion-api-response-json">{requestDebug.curlPreview}</pre>
        </>
      )}
      {responseBody && (
        <>
          <strong>Response body</strong>
          <pre className="ingestion-api-response-json">{responseBody}</pre>
        </>
      )}
    </details>
  );
}

function StepSummary({ step }) {
  const d = step.data || {};
  const { rawResponse, extractedItems, requestDebug, responseBody, ...meta } = d;

  return (
    <>
      <RequestDebugBlock
        requestDebug={requestDebug || meta.requestDebug}
        responseBody={responseBody}
        httpStatus={meta.httpStatus}
        defaultOpen={!step.success}
      />
      {step.success && (
        <ul className="ingestion-api-step-summary">
          {meta.dataPathUsed && <li><strong>Data path</strong> <code>{meta.dataPathUsed}</code></li>}
          {meta.httpStatus != null && <li><strong>HTTP</strong> {meta.httpStatus}</li>}
          {meta.recordCount != null && (
            <li>
              <strong>Records</strong> {meta.recordCount}
              {meta.totalResults != null ? ` (API total: ${meta.totalResults})` : ''}
            </li>
          )}
          {meta.sampleTitle && <li><strong>Sample</strong> {meta.sampleTitle}</li>}
        </ul>
      )}
      {rawResponse != null && step.success && (
        <details className="ingestion-api-response-block">
          <summary>Full API response</summary>
          <pre className="ingestion-api-response-json">{JSON.stringify(rawResponse, null, 2)}</pre>
        </details>
      )}
      {Array.isArray(extractedItems) && extractedItems.length > 0 && (
        <details className="ingestion-api-response-block">
          <summary>Extracted ingestion items ({extractedItems.length})</summary>
          <pre className="ingestion-api-response-json">{JSON.stringify(extractedItems, null, 2)}</pre>
        </details>
      )}
    </>
  );
}

function StepRow({ step }) {
  const label = step.groupName && step.groupName !== step.step
    ? `${step.groupName} (${step.step})`
    : step.step;

  return (
    <div className={`ingestion-api-step ${step.success ? 'ok' : 'fail'}`}>
      <div className="ingestion-api-step-head">
        <span className="ingestion-api-step-name">
          <i className={`fas fa-${step.success ? 'check-circle' : 'times-circle'}`} />
          {label}
        </span>
        <span className="ingestion-api-step-ms">{step.durationMs} ms</span>
      </div>
      <StepSummary step={step} />
      {step.error && <pre className="ingestion-api-step-error">{step.error}</pre>}
    </div>
  );
}

function SystemIngestionApiTestPanel({ setMessage }) {
  const [running, setRunning] = useState(false);
  const [loadingSources, setLoadingSources] = useState(true);
  const [result, setResult] = useState(null);
  const [suffix, setSuffix] = useState('');
  const [apiGroups, setApiGroups] = useState([]);
  const [groupSlug, setGroupSlug] = useState('');
  const [queryParamRows, setQueryParamRows] = useState(() => [createEditorRow()]);
  const [headerParamRows, setHeaderParamRows] = useState(() => [createEditorRow()]);
  const [bodyParamRows, setBodyParamRows] = useState(() => [createEditorRow()]);
  const harnessReadyRef = useRef(false);

  const selectedGroup = useMemo(
    () => apiGroups.find((g) => (g.group_slug || g.slug) === groupSlug) || null,
    [apiGroups, groupSlug]
  );

  const selectedApiSource = useMemo(
    () => selectedGroup?.config?.api_source || {},
    [selectedGroup]
  );
  const httpMethod = resolveHttpMethod(selectedApiSource);

  const previewApiSource = useMemo(() => ({
    endpoint: selectedApiSource.endpoint || '',
    http_method: httpMethod,
    auth_type: selectedApiSource.auth_type || 'none',
    query_param_defs: selectedApiSource.query_param_defs || [],
    header_param_defs: selectedApiSource.header_param_defs || [],
    body_param_defs: selectedApiSource.body_param_defs || [],
  }), [selectedApiSource, httpMethod]);

  const applyGroupHarness = useCallback((group, horizonSuffix) => {
    const slug = group?.group_slug || group?.slug || '';
    const apiSrc = group?.config?.api_source || {};
    const stored = loadHarnessState(horizonSuffix, slug);
    const { queryRows, headerRows, bodyRows } = harnessRowsFromGroup(apiSrc, stored);
    const empty = emptyHarnessRows();

    harnessReadyRef.current = false;
    setQueryParamRows(queryRows.length ? queryRows : empty.queryRows);
    setHeaderParamRows(headerRows.length ? headerRows : empty.headerRows);
    setBodyParamRows(bodyRows.length ? bodyRows : empty.bodyRows);
    harnessReadyRef.current = true;
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingSources(true);
        const page = await fetchIngestionSettingsSourcesPage(1, 100, { horizonScope: 'active' });
        if (!mounted) return;
        const items = Array.isArray(page?.items) ? page.items : [];
        const activeSuffix = page?.activeSuffix || page?.suffix || '';
        setSuffix(activeSuffix);
        const apiOnly = items.filter((g) => String(g?.config?.type || '').toLowerCase() === 'api_source');
        setApiGroups(apiOnly);
        if (apiOnly.length) {
          const first = apiOnly[0];
          const slug = first.group_slug || first.slug || '';
          setGroupSlug(slug);
          applyGroupHarness(first, activeSuffix);
        }
      } catch (_) {
        if (mounted) setApiGroups([]);
      } finally {
        if (mounted) setLoadingSources(false);
      }
    })();
    return () => { mounted = false; };
  }, [applyGroupHarness]);

  useEffect(() => {
    if (!groupSlug || !suffix) return;
    const group = apiGroups.find((g) => (g.group_slug || g.slug) === groupSlug);
    if (group) applyGroupHarness(group, suffix);
  }, [groupSlug, apiGroups, suffix, applyGroupHarness]);

  useEffect(() => {
    if (!groupSlug || !suffix || !harnessReadyRef.current) return;
    saveHarnessState(suffix, groupSlug, harnessDeltaFromRows(selectedApiSource, {
      queryRows: queryParamRows,
      headerRows: headerParamRows,
      bodyRows: bodyParamRows,
    }));
  }, [queryParamRows, headerParamRows, bodyParamRows, groupSlug, suffix, selectedApiSource]);

  const handleRun = async () => {
    try {
      setRunning(true);
      setMessage(null);
      setResult(null);
      const paramOverrides = harnessOverridesFromRows({
        queryRows: queryParamRows,
        headerRows: headerParamRows,
        bodyRows: bodyParamRows,
      });
      const payload = await runIngestionApiTest({
        groupSlug: groupSlug.trim() || undefined,
        paramOverrides,
      });
      setResult(payload);
      const failed = (payload.steps || []).filter((s) => !s.success).length;
      const ok = (payload.steps || []).filter((s) => s.success).length;
      setMessage({
        type: failed > 0 ? 'error' : 'success',
        text: failed > 0
          ? `API source test: ${ok} passed, ${failed} failed on horizon "${payload.suffix || 'unknown'}".`
          : `API source test passed for ${ok} group${ok === 1 ? '' : 's'} on horizon "${payload.suffix || 'unknown'}".`,
      });
    } catch (err) {
      if (err.result) {
        setResult(err.result);
      }
      setMessage({
        type: 'error',
        text: err.message || 'Ingestion API test failed',
        details: err.details || null,
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="ingestion-api-test-panel web-search-test-panel">
      <p className="panel-intro">
        Run a <strong>live HTTP call</strong> against API ingestion groups on your active data horizon.
        Override params, headers, auth (via group settings), and body below. Results include the full request sent.
      </p>

      <div className="web-search-test-form">
        <div className="form-group">
          <label htmlFor="ingestionApiGroupSlug">API ingestion group</label>
          {loadingSources ? (
            <div className="text-muted"><i className="fas fa-spinner fa-spin" /> Loading groups…</div>
          ) : (
            <select
              id="ingestionApiGroupSlug"
              className="form-control"
              value={groupSlug}
              onChange={(e) => setGroupSlug(e.target.value)}
              disabled={running || !apiGroups.length}
            >
              {!apiGroups.length && <option value="">No API ingestion groups</option>}
              {apiGroups.map((g) => {
                const slug = g.group_slug || g.slug;
                return (
                  <option key={slug} value={slug}>{g.name || slug}</option>
                );
              })}
            </select>
          )}
        </div>

        {selectedGroup && (
          <div className="form-group">
            <label>Request overrides (method: {httpMethod})</label>
            <ApiSourceRequestTabs
              httpMethod={httpMethod}
              endpoint={selectedApiSource.endpoint || ''}
              previewApiSource={previewApiSource}
              queryParamRows={queryParamRows}
              onQueryParamRowsChange={setQueryParamRows}
              headerParamRows={headerParamRows}
              onHeaderParamRowsChange={setHeaderParamRows}
              bodyParamRows={bodyParamRows}
              onBodyParamRowsChange={setBodyParamRows}
              authType={selectedApiSource.auth_type || 'none'}
              authReadOnly
              showBodyTab={httpMethod === 'POST'}
            />
            <div className="hint">
              Auth is configured in <strong>Settings → Ingestion</strong> (Auth tab). Harness shows whether auth was applied.
            </div>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleRun}
          disabled={running || !apiGroups.length || !groupSlug}
        >
          {running ? (
            <><i className="fas fa-spinner fa-spin" /> Calling API…</>
          ) : (
            <><i className="fas fa-network-wired" /> Run API source test</>
          )}
        </button>
      </div>

      {result && (
        <div className="ingestion-api-test-results">
          <h4>
            Results
            {result.suffix ? <span className="ingestion-api-horizon"> — horizon: {result.suffix}</span> : null}
          </h4>
          {(result.steps || []).map((step) => (
            <StepRow key={step.step} step={step} />
          ))}
        </div>
      )}
    </div>
  );
}

export default SystemIngestionApiTestPanel;
